import React, { useCallback, useEffect, useState } from 'react';
import Module from '../../../../../layout/Module';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate, useParams, Routes, Route, useLocation } from 'react-router-dom';
import {
  updatePeriodicalReportForm,
  updatePeriodicalReportFormField
} from '../../../../../../store/actions/periodical-report.actions';
import { handlerErrorV2 } from '../../../../../../utils/errors';
import { Alert } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import FormBasicInformation from '../FormBasicInformation/FormBasicInformation';
import { toggleAlertModule } from '../../../../../../store/actions/alert.actions';
import AlertSaveButton from '../../../../../button/AlertSaveButton';
import ActionsButton from '../ActionsButton/ActionsButton';
import ButtonStartIcon from '../../../../../button/ButtonStartIcon';
import usePeriodicalReport from '../../../../../../hooks/periodical-report/usePeriodicalReport';
import TabsLink from '../../../../../tabs/TabsLink';
import ListAssessmentArea from '../ListAssessmentArea/ListAssessmentArea';
import InfoCurrentPeriodicalReport from '../../InfoCurrentPeriodicalReport/InfoCurrentPeriodicalReport';
import FormLogsStatus from '../FormLogsStatus/FormLogsStatus';
import FormSyncHistory from '../FormSyncHistory/FormSyncHistory';

const EditPreview = () => {
  const { periodicalReportId } = useParams();
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, toggleLoading] = useState(false);
  const [isLoadingSave, toggleIsLoadingSave] = useState(false);
  const [error, setError] = useState(null);
  const permissions = useSelector(
    (state) => state?.session?.permissions?.['periodical-report-generator'] || {}
  );
  const [tabLogs, setTabLogs] = useState('status');

  const { updatePeriodicalReport, getPeriodicalReportById } = usePeriodicalReport();
  const formData = useSelector((state) => state?.periodicalReport?.form || {});
  const pathIsPreview = pathname?.includes('preview');
  const isPreview = pathIsPreview || formData?.status !== 'draft';
  const alertSave = useSelector((state) => state?.alert?.save || {});

  const onCancel = () => {
    navigate(`/dashboard/periodical-report`);
  };

  const onSave = async (e) => {
    e.preventDefault();
    toggleIsLoadingSave(true);
    try {
      let result = await updatePeriodicalReport(periodicalReportId, 'basic-information', formData);
      if (result.error) {
        setError(result);
      }
    } finally {
      toggleIsLoadingSave(false);
    }
  };

  const onChange = (key, value) => {
    dispatch(updatePeriodicalReportFormField(key, value));
    setError({ ...error, [key]: null });

    if (!alertSave?.mode || !alertSave?.onSave) {
      dispatch(toggleAlertModule(false, 'Periodical report', 'edit', onSave, alertSave?.onDontSave));
    }
  };

  const onLoad = useCallback(async () => {
    if (periodicalReportId) {
      toggleLoading(true);
      try {
        const response = await getPeriodicalReportById(`${periodicalReportId}?previousfull=true`);
        if (response?.error) {
          setError({
            message: response.error.message || 'An error ocurred'
          });
        }
      } catch (error) {
        const message = handlerErrorV2(error);
        setError(message);
      } finally {
        toggleLoading(false);
      }
    }
  }, [periodicalReportId]);

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary justify-between relative'>
        <InfoCurrentPeriodicalReport isLoading={loading} currentPeriodicalReport={formData} />
        <div
          className='flex gap-2 justify-between items-center absolute top-0 bottom-0px right-4'
          style={{
            left: 'calc(180px + 2.5rem)'
          }}
        >
          <div className='relative w-full h-full'>
            <TabsLink
              id='school'
              className='bottom--1px'
              items={[
                {
                  id: 'basic-information',
                  name: 'Basic information',
                  path: `/dashboard/periodical-report/${
                    pathIsPreview ? 'preview' : 'edit'
                  }/${periodicalReportId}`,
                  permission: true,
                  end: true
                },
                {
                  id: 'assessment-area',
                  name: 'Assessment areas',
                  path: `/dashboard/periodical-report/${
                    pathIsPreview ? 'preview' : 'edit'
                  }/${periodicalReportId}/assessment-area`,
                  permission: true,
                  end: true
                }
              ]}
            />
          </div>
          <div className='flex items-center justify-end gap-2'>
            <ActionsButton
              formData={formData}
              error={error}
              loading={loading}
              isLoadingSave={isLoadingSave}
            />
            <AlertSaveButton
              submodule='Periodical report'
              disabled={loading || isLoadingSave}
              icon={['fal', 'times']}
              title='Cancel'
              color='primary'
              variant='outlined'
              className='shadow-sm'
              onDontSave={onCancel}
            />
          </div>
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer
        loading={isLoadingSave}
        classNameWrapper={isLoadingSave || loading ? '' : 'overflow-auto'}
      >
        {error?.message && (
          <Alert severity='error' onClose={() => setError(null)}>
            {error?.message}
          </Alert>
        )}
        <Box className='absolute inset-0 flex flex-col'>
          <Routes>
            <Route
              index
              element={
                <>
                  <Box
                    className={[
                      'bg-primary text-primary-foreground',
                      'px-4 py-1 flex justify-between items-center gap-1 h-8'
                    ].join(' ')}
                  >
                    <Typography variant='subtitle1' className='uppercase'>
                      Basic information
                    </Typography>
                    <div className='flex justify-end gap-1 items-center'>
                      {permissions.updated && !isPreview && (
                        <ButtonStartIcon
                          disabled={isLoadingSave || loading}
                          loading={isLoadingSave}
                          onClick={onSave}
                          startIcon={['fal', 'check']}
                          color='secondary'
                        >
                          Save
                        </ButtonStartIcon>
                      )}
                    </div>
                  </Box>
                  <Box className='py-4'>
                    <FormBasicInformation
                      pathIsPreview={pathIsPreview}
                      isPreview={isPreview}
                      isLoading={loading}
                      form={formData}
                      errors={error || {}}
                      onChange={onChange}
                    />
                  </Box>
                  <Box className='border-b border-primary flex justify-start items-center gap-2 h-8'>
                    <div
                      onClick={loading ? undefined : () => setTabLogs('status')}
                      className={[
                        ' px-4  border border-primary h-8 flex justify-start items-center uppercase',
                        loading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                        tabLogs === 'status'
                          ? 'bg-primary text-primary-foreground font-bold'
                          : 'text-primary bg-clickeabled'
                      ].join(' ')}
                      style={{
                        borderTopLeftRadius: '0.5rem',
                        borderTopRightRadius: '0.5rem'
                      }}
                    >
                      Logs Status
                    </div>
                    <div
                      onClick={loading ? undefined : () => setTabLogs('sync')}
                      className={[
                        ' px-4  border border-primary h-8 flex justify-start items-center uppercase',
                        loading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                        tabLogs === 'sync'
                          ? 'bg-primary text-primary-foreground font-bold'
                          : 'text-primary bg-clickeabled'
                      ].join(' ')}
                      style={{
                        borderTopLeftRadius: '0.5rem',
                        borderTopRightRadius: '0.5rem'
                      }}
                    >
                      Sync History
                    </div>
                  </Box>
                  <Box className='absolute inset-x-2 top-100 bottom-0'>
                    {tabLogs === 'status' && (
                      <FormLogsStatus isLoading={loading} data={formData?.logsStatus || []} />
                    )}
                    {tabLogs === 'sync' && <FormSyncHistory />}
                  </Box>
                </>
              }
            />
            <Route
              path='assessment-area'
              element={
                <ListAssessmentArea
                  isPreview={isPreview}
                  isLoading={loading}
                  data={formData?.assessmentAreas || []}
                  currentPeriodicalReport={formData}
                />
              }
            />
          </Routes>
        </Box>
      </Module.BodyContainer>
    </>
  );
};

export default EditPreview;
